<script lang="ts">
	import type { SvelteAsr } from 'client/types/common'
	import { onMount, type ComponentProps } from 'svelte'
	import { graphql, type Reports$result } from '$houdini'
	import { ReportViewerPreview } from '@isoftdata/svelte-report-viewer'
	import ReportJobModal from 'components/ReportJobModal.svelte'
	import Button from '@isoftdata/svelte-button'

	interface Props {
		asr: SvelteAsr
		selectedReportId: number | null
		reportList: Reports$result['reports']['items']
		parameters: any
		preview?: boolean
	}

	let { asr, selectedReportId = $bindable(), reportList, parameters, preview = false }: Props = $props()

	let reportViewerPreview: ReportViewerPreview = $state()!
	let reportJobModal: ReportJobModal | undefined = $state(undefined)!
	let allowPrint = $state(false)

	const generateCrystalReport = graphql(`
		mutation GenerateCrystalReport($input: GenerateCrystalReportInput!) {
			generateCrystalReport(input: $input) {
				data
				mimeType
			}
		}
	`)

	async function generatePdfPreview({
		name,
		type,
		parameters,
	}: Parameters<ComponentProps<typeof ReportViewerPreview>['generatePdfPreview']>[0]) {
		allowPrint = false
		const { data } = await generateCrystalReport.mutate({
			input: {
				name,
				parameters: Object.entries(parameters).map(([key, value]) => ({ key, value })),
				type,
				id: selectedReportId,
			},
		})

		if (!data) {
			throw new Error('No data returned from generateCrystalReport')
		}
		allowPrint = true
		return data.generateCrystalReport
	}

	function onPrintClick() {
		const report = reportList.find(r => r.id === selectedReportId)
		if (report && selectedReportId) {
			reportJobModal?.open(
				{
					name: report.name,
					type: report.type,
					reportId: selectedReportId,
					parameters: parameters.reduce((acc: Record<string, string>, param) => {
						acc[param.name] = param.value
						return acc
					}, {}),
				},
				{ report },
			)
		}
	}

	onMount(() => {
		if (preview) {
			reportViewerPreview.doPreview()
			asr.go(null, { preview: undefined }, { inherit: true })
		}
	})
</script>

{#if selectedReportId}
	<ReportViewerPreview
		bind:this={reportViewerPreview}
		bind:selectedReportId
		{generatePdfPreview}
		{parameters}
		{reportList}
	>
		{#snippet previewFooter()}
			<Button
				outline
				size="sm"
				iconClass="print"
				disabled={!selectedReportId || !allowPrint}
				onclick={() => onPrintClick()}>Print</Button
			>
		{/snippet}
	</ReportViewerPreview>
{/if}

<ReportJobModal
	showPreview={false}
	bind:this={reportJobModal}
/>
